import { ActionIcon, Box, Tooltip, VisuallyHidden } from "@mantine/core";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";

import type { StreamStatus } from "../../api/events";
import type { TeamRole } from "../../api/generated/model";
import { GiftMenu } from "../case/GiftMenu";
import { UiSwitch } from "../ui-switch/UiSwitch";
import { Breadcrumbs } from "./Breadcrumbs";
import { UsagePill } from "./UsagePill";
import { LogoLink } from "./LogoLink";
import classes from "./shell.module.css";
import { UserMenu, useSchemeSwitch } from "./UserMenu";

/** What the shell shows of the team on screen; TeamLayout owns the stream (F3 decision 29). */
export interface ShellTeam {
  teamId: string;
  role: TeamRole;
  streamStatus: StreamStatus;
}

const STREAM_WORD: Record<StreamStatus, string> = {
  connecting: "Connecting",
  live: "Live",
  reconnecting: "Reconnecting",
};

/**
 * F3's stream status as a dot, shown only while the stream reconnects: a healthy header looks as
 * live's does (decision 37). The `<output>` stays in the page as a status region either way: its
 * hidden word is what a screen reader announces when it changes; the label names it for the smoke.
 */
function StreamDot({ status }: { status: StreamStatus }) {
  const label = `Updates: ${STREAM_WORD[status]}`;
  return (
    <Tooltip label={label}>
      <output aria-label={label} className={classes.dot} data-status={status}>
        <VisuallyHidden>{label}</VisuallyHidden>
      </output>
    </Tooltip>
  );
}

function ThemeToggle() {
  const { Icon, label, flip } = useSchemeSwitch();
  return (
    <Tooltip label={label} position="bottom" openDelay={200}>
      <ActionIcon
        variant="subtle"
        size="lg"
        radius="md"
        aria-label={label}
        visibleFrom="md"
        onClick={flip}
      >
        <Icon size={18} className={classes.themeIcon} />
      </ActionIcon>
    </Tooltip>
  );
}

interface HeaderProps {
  team: ShellTeam | undefined;
  sidebarOpen: boolean;
  /** Absent outside a team's frame, which has no sidebar. */
  onToggleSidebar: (() => void) | undefined;
  /** Covered by a full-window page: out of the tab order and of a screen reader's reading. */
  inert?: boolean;
}

/**
 * Spec 2.2: the toggle, logo and crumbs on the left; on the right the stream's dot while it
 * reconnects (ours, so it sits apart from live's group), the theme, the credits and the account.
 */
export function Header({ team, sidebarOpen, onToggleSidebar, inert = false }: HeaderProps) {
  const Toggle = sidebarOpen ? PanelLeftClose : PanelLeftOpen;
  return (
    <header className={classes.header} inert={inert}>
      <div className={classes.headerLeft}>
        {onToggleSidebar === undefined ? null : (
          <ActionIcon
            variant="transparent"
            className={classes.toggle}
            aria-label={sidebarOpen ? "Collapse the sidebar" : "Expand the sidebar"}
            aria-expanded={sidebarOpen}
            onClick={onToggleSidebar}
          >
            <Toggle size={20} strokeWidth={1.5} className={classes.toggleIcon} />
          </ActionIcon>
        )}
        <LogoLink teamId={team?.teamId} />
        {/* Classic, Simple or Pro (spec ui-perf/ui-switch-classic-simple-pro). */}
        {/* On a phone it is in the account menu: the header has no room. */}
        {team === undefined ? null : (
          <Box visibleFrom="sm">
            <UiSwitch teamId={team.teamId} />
          </Box>
        )}
        {team === undefined ? null : (
          // From `lg` (74em, 1184 px, platform/theme.ts): from `md` (64em) the crumbs ran under the
          // theme toggle and the credits (spec ui-perf/classic-header-widths).
          <Box visibleFrom="lg">
            <Breadcrumbs teamId={team.teamId} />
          </Box>
        )}
      </div>
      <div className={classes.headerRight}>
        {team === undefined ? null : (
          <Box
            visibleFrom="sm"
            className={team.streamStatus === "reconnecting" ? undefined : classes.quiet}
          >
            <StreamDot status={team.streamStatus} />
          </Box>
        )}
        <ThemeToggle />
        {team === undefined ? null : <GiftMenu teamId={team.teamId} />}
        {team === undefined ? null : <UsagePill teamId={team.teamId} />}
        <UserMenu teamId={team?.teamId} role={team?.role} />
      </div>
    </header>
  );
}
